/* eslint-disable @typescript-eslint/no-explicit-any */
import { Stack } from '@fluentui/react';
import { useMachine } from '@xstate/react';
import React, { useEffect, useRef, useState } from 'react';
import { State, StateMachine } from 'xstate';
import {
  generateSuccessMessage,
  getComponentByName,
  Input,
  Invoker,
  RemoteInput,
  SuccessMessage,
} from './components';
import { SpinnerLoader } from './components/Invoker/Loader';
import { BaseContext, BaseEvent } from './machine';

export const stateTypes = ['remote_input', 'success_message', 'custom_element'] as const;
export type StateType = typeof stateTypes[number];

export type OnSubmitHistory = (data: {
  stateName: string;
  stateType?: StateType;
  stateMessage?: string;
}) => Promise<void>;

export interface FlowRunnerProps {
  logo?: string;
  stateMachine: StateMachine<
    BaseContext,
    any,
    BaseEvent,
    {
      value: any;
      context: BaseContext;
    }
  >;
  onSubmit?: (stateName: string, inputs: Input[]) => Promise<void>;
  onSyncHistory?: OnSubmitHistory;
}

export const FlowRunner = (props: FlowRunnerProps): JSX.Element => {
  const { logo, stateMachine, onSubmit } = props;
  const logoProps = logo ? { logo } : {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showLoader, setShowLoader] = useState(false);
  const [currentState, send] = useMachine(stateMachine);
  const [history, setHistory] = useState<
    Array<
      State<
        BaseContext,
        BaseEvent,
        any,
        {
          value: any;
          context: BaseContext;
        }
      >
    >
  >([]);
  const messagesEnd = useRef<HTMLDivElement>(null);

  const syncHistoryAndMoveForward = React.useCallback(async () => {
    const historyObject = convertToHistoryObject(currentState);
    console.log('Sync History', historyObject);
    // setShowLoader(true);
    props.onSyncHistory && (await props.onSyncHistory(historyObject));
    // setShowLoader(false);
  }, [currentState]);

  useEffect(() => {
    messagesEnd?.current?.scrollIntoView({ behavior: 'smooth' });
  });

  useEffect(() => {
    if (history.length === 0 || !history[history.length - 1].matches(currentState.value)) {
      (async () => {
        setHistory([...history, currentState]);
        await syncHistoryAndMoveForward();
      })();
    }
  }, [currentState]);

  useEffect(() => {
    console.log('Historical States Updated', history);
  }, [history]);

  const messages = history.map((state, index) => (
    <Stack key={index}>
      {state.hasTag('remote_input') ? (
        <RemoteInput state={state} {...logoProps}>
          <Invoker
            {...logoProps}
            currentParentState={currentState.value.toString()}
            element={getComponentByName(state.context.component)}
            elementProps={{ inputs: state.context.componentInputs }}
            onSuccess={(data) => send('SUCCESS', data)}
            onSubmit={onSubmit}
          />
        </RemoteInput>
      ) : state.hasTag('success_message') ? (
        <SuccessMessage state={state} parent={state?.meta[state.value.toString()]?.parent} />
      ) : state.hasTag('custom_element') ? (
        state.meta[state.value.toString()].jsx
      ) : undefined}
    </Stack>
  ));

  return (
    <Stack>
      {messages}
      {(currentState.context.loading || showLoader) && <SpinnerLoader />}
      <div style={{ float: 'left', clear: 'both', paddingBottom: '4rem' }} ref={messagesEnd}></div>
    </Stack>
  );
};

const convertToHistoryObject = (
  state: State<
    BaseContext,
    BaseEvent,
    any,
    {
      value: any;
      context: BaseContext;
    }
  >
): {
  stateName: string;
  stateType?: StateType;
  stateMessage?: string;
} => {
  let stateMessage =
    state.hasTag('remote_input') || state.hasTag('success_message')
      ? generateSuccessMessage(state, state.meta[state.value.toString()]?.parent)
      : undefined;

  if (typeof stateMessage !== 'string') {
    stateMessage = undefined;
  }

  return {
    stateName: state.value.toString(),
    stateType: state.hasTag('remote_input')
      ? 'remote_input'
      : state.hasTag('success_message')
        ? 'success_message'
        : state.hasTag('custom_element')
          ? 'custom_element'
          : undefined,
    stateMessage: stateMessage,
  };
};
